import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import React, { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { addBookmark, openBookmarkLoginRequiredModal, removeBookmark } from '../../../ducks/bookmarks.duck';
import useAppSelector from '../../../util/hooks/useAppSelector';
import IconStar from '../../Icons/IconStar';

const REVIEWS_SCROLL_OFFSET = 80;

// The rating comes from listing metadata, while the reviews section only mounts once public
// reviews are loaded — the ref can still be empty when the rating is tapped (VANSITE-FRONTEND-4S6).
const scrollToReviews = (reviewsSection) => {
  if (!reviewsSection) return;
  const bodyTop = document.body.getBoundingClientRect().top;
  const sectionTop = reviewsSection.getBoundingClientRect().top;
  window.scrollTo({
    behavior: 'smooth',
    top: sectionTop - bodyTop - REVIEWS_SCROLL_OFFSET,
  });
};

const ListingFunctionsSectionComponent = ({ currentListing, onShareListing, reviewsRef }) => {
  const dispatch = useDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const currentBookmarks = useAppSelector((state) => state.bookmarks.currentBookmarks);
  const listingUUID = currentListing.id.uuid;
  const { rating, ratingCount } = currentListing.attributes.metadata || {
    rating: 0,
    ratingCount: 0,
  };

  const bookmarked = currentBookmarks.includes(listingUUID);

  const onAddBookmark = useCallback(() => {
    if (isAuthenticated) {
      dispatch(addBookmark(listingUUID));
    } else {
      dispatch(openBookmarkLoginRequiredModal());
    }
  }, [dispatch, isAuthenticated, listingUUID]);

  const onRemoveBookmark = useCallback(() => {
    if (isAuthenticated) {
      dispatch(removeBookmark(listingUUID));
    } else {
      dispatch(openBookmarkLoginRequiredModal());
    }
  }, [dispatch, isAuthenticated, listingUUID]);

  const handleScrollToReview = () => scrollToReviews(reviewsRef?.current);

  return (
    <Box display="flex" sx={{ justifyContent: { xs: 'space-between', md: 'flex-start' } }}>
      {ratingCount && ratingCount > 0 ? (
        <Box alignItems="center" display="flex" flexDirection="row" onClick={handleScrollToReview}>
          <IconStar filled />
          <Typography variant="body1">{rating ? (rating / 100).toFixed(2) : 0}</Typography>
          <Typography ml={1} variant="body1">
            ({ratingCount ? ratingCount : 0})
          </Typography>
        </Box>
      ) : null}
      <Button onClick={onShareListing} startIcon={<ShareOutlinedIcon />}>
        <Typography variant="body2">
          <FormattedMessage id="ListingPage.socialShare" />
        </Typography>
      </Button>
      <Button
        onClick={bookmarked ? onRemoveBookmark : onAddBookmark}
        startIcon={bookmarked ? <FavoriteIcon /> : <FavoriteBorderOutlinedIcon />}
      >
        <Typography variant="body2">
          <FormattedMessage id="ListingPage.saveBookmark" />
        </Typography>
      </Button>
    </Box>
  );
};

ListingFunctionsSectionComponent.displayName = 'ListingFunctionsSection';

const ListingFunctionsSection = memo(ListingFunctionsSectionComponent);

export default ListingFunctionsSection;
